import React, { useEffect, useMemo, useState } from 'react';
import { gsGetColumns, gsGetTabs } from '../../utils/api';
import GenericDropdown from '../GenericDropdown';

// Google Spreadsheet config editor
// props: value (config object), onChange(cfg)
const GoogleSpreadsheetConfig = ({ value = {}, onChange }) => {
  const [spreadsheetId, setSpreadsheetId] = useState(value.spreadsheet_id || '');
  const [sheetName, setSheetName] = useState(value.sheet_name || '');
  const [columnMapping, setColumnMapping] = useState(Array.isArray(value.column_mapping) ? value.column_mapping : []);
  // Preserve group-level dedupe when this source editor re-emits config (do not clobber).
  const dedupe = value?.dedupe;
  const [tabs, setTabs] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [error, setError] = useState('');
  const [mappingError, setMappingError] = useState('');

  useEffect(() => {
    onChange && onChange({
      type: 'google_spreadsheet',
      spreadsheet_id: spreadsheetId,
      sheet_name: sheetName || undefined,
      column_mapping: columnMapping,
      ...(dedupe && typeof dedupe === 'object' ? { dedupe } : {}),
    });
  }, [spreadsheetId, sheetName, columnMapping, onChange, dedupe]);

  // Load tabs when spreadsheetId changes
  useEffect(() => {
    if (!spreadsheetId) { setTabs([]); return; }
    let active = true;
    setLoadingTabs(true);
    setError('');
    (async () => {
      try {
        // Validate and get tabs (either endpoint is fine; use tabs directly)
        const list = await gsGetTabs(spreadsheetId);
        if (!active) return;
        setTabs(list);
        // If no explicit sheet selected, keep existing; if not in list, clear selection
        if (sheetName && !list.includes(sheetName)) {
          setSheetName('');
        }
      } catch (e) {
        if (!active) return;
        setTabs([]);
        setError(e.message || 'Failed to load spreadsheet tabs');
      } finally {
        if (active) setLoadingTabs(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spreadsheetId]);

  // Load columns when spreadsheetId or sheetName changes
  useEffect(() => {
    if (!spreadsheetId) { setColumns([]); return; }
    let active = true;
    setLoadingColumns(true);
    setError('');
    (async () => {
      try {
        const { columns: cols, sheet_name: resolved } = await gsGetColumns(spreadsheetId, sheetName);
        if (!active) return;
        setColumns(cols || []);
        if (!sheetName && resolved) setSheetName(resolved);
      } catch (e) {
        if (!active) return;
        setColumns([]);
        setError(e.message || 'Failed to load columns');
      } finally {
        if (active) setLoadingColumns(false);
      }
    })();
    return () => { active = false; };
  }, [spreadsheetId, sheetName]);

  const addMapping = () => {
    setColumnMapping(m => [...m, { column: '', slug: '', link: false }]);
  };
  const updateMapping = (idx, patch) => {
    setColumnMapping(m => m.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
  };
  const removeMapping = (idx) => {
    setColumnMapping(m => m.filter((_, i) => i !== idx));
  };

  const columnOptions = useMemo(() => (columns || []).map(c => ({ id: c, name: c })), [columns]);

  // Utilities for slug handling
  const sanitizeSlug = (input) => {
    const s = (input || '').toString().toLowerCase();
    // Replace any sequence of non a-z0-9 with underscore
    let out = s.replace(/[^a-z0-9]+/g, '_');
    // Collapse multiple underscores and trim from ends
    out = out.replace(/_+/g, '_').replace(/^_+|_+$/g, '');
    return out;
  };

  // Ensure unique slug by appending numeric suffix when needed
  const makeUniqueSlug = (base, used) => {
    let b = base || 'col';
    if (!b.trim()) b = 'col';
    let candidate = b;
    let i = 2;
    while (used.has(candidate)) {
      candidate = `${b}_${i++}`;
    }
    used.add(candidate);
    return candidate;
  };

  // Add all columns helper
  const addAllColumns = () => {
    if (!columns || !columns.length) return;
    setColumnMapping(prev => {
      const next = [...prev];
      const usedSlugs = new Set((prev || []).map(r => (r.slug || '').trim()).filter(Boolean));
      const existingByColumn = new Set((prev || []).map(r => (r.column || '')));
      const added = [];
      for (const col of columns) {
        if (existingByColumn.has(col)) continue; // skip if already mapped
        const base = sanitizeSlug(col) || 'col';
        const uniqueSlug = makeUniqueSlug(base, usedSlugs);
        const row = { column: col, slug: uniqueSlug, link: false };
        next.push(row);
        added.push(row);
      }
      // Update mapping error based on duplicates after operation
      validateDuplicateSlugs(next);
      return next;
    });
  };

  const validateDuplicateSlugs = (mapping) => {
    const counts = new Map();
    for (const r of mapping || []) {
      const s = (r.slug || '').trim();
      if (!s) continue;
      counts.set(s, (counts.get(s) || 0) + 1);
    }
    const dups = Array.from(counts.entries()).filter(([, c]) => c > 1).map(([s]) => s);
    if (dups.length) {
      setMappingError(`Duplicate slugs found: ${dups.join(', ')}`);
    } else {
      setMappingError('');
    }
  };

  // Watch mapping to flag duplicate slugs users might create manually
  useEffect(() => {
    validateDuplicateSlugs(columnMapping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnMapping]);

  return (
    <div className="gs-config">
      <div className="field-row">
        <label>Spreadsheet ID <span className="required">*</span></label>
        <input
          type="text"
          value={spreadsheetId}
          onChange={e => {
            const raw = e.target.value.trim();
            // Auto-extract spreadsheet ID from a full Google Sheets URL
            const match = raw.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
            setSpreadsheetId(match ? match[1] : raw);
          }}
          placeholder="Paste URL or ID, e.g. 1a2B3cD4..."
        />
        {loadingTabs && <span className="hint"><i className="fas fa-spinner fa-spin"></i> Validating…</span>}
      </div>

      <div className="field-row">
        <label>Tab (Sheet)</label>
        <GenericDropdown
          items={(tabs || []).map(t => ({ id: t, name: t }))}
          value={sheetName || ''}
          onChange={(id) => setSheetName(id || '')}
          placeholder={tabs.length ? 'Select a tab' : 'No tabs'}
          disabled={!tabs.length}
          allowNone={true}
          noneLabel="Use first tab"
        />
      </div>

      <div className="field-row">
        <label>Mode <span className="hint">(deprecated)</span></label>
        <div className="hint">
          Per-sheet Add only / Sync is deprecated. Use the group <strong>Deduplication</strong> setting
          (Add / Skip Duplicates / Update Duplicates / Sync) with a Unique key such as <code>id</code>.
        </div>
      </div>

      <div className="field-row">
        <label>Column Mapping</label>
        {loadingColumns && <div className="hint"><i className="fas fa-spinner fa-spin"></i> Loading columns…</div>}
        {!loadingColumns && (
          <div className="mapping-list">
            {columnMapping.map((row, idx) => (
              <div key={idx} className="mapping-row">
                <div className="col">
                  <div className="lbl">Sheet Column</div>
                  <GenericDropdown
                    items={columnOptions}
                    value={row.column || ''}
                    onChange={(id) => {
                      const next = { column: id };
                      // If slug is empty, prepopulate based on selected column
                      const currentSlug = (row.slug || '').trim();
                      if (!currentSlug) {
                        next.slug = sanitizeSlug(id);
                      }
                      updateMapping(idx, next);
                    }}
                    placeholder="Search or select a column"
                    disabled={!columns.length}
                  />
                </div>
                <div className="col">
                  <div className="lbl">Slug</div>
                  <input
                    type="text"
                    value={row.slug || ''}
                    onChange={e => {
                      const raw = e.target.value;
                      const cleaned = sanitizeSlug(raw);
                      updateMapping(idx, { slug: cleaned });
                    }}
                    placeholder="e.g. job_title"
                  />
                </div>
                <div className="col small">
                  <div className="lbl">Link</div>
                  <input
                    type="checkbox"
                    checked={!!row.link}
                    onChange={e => updateMapping(idx, { link: !!e.target.checked })}
                  />
                </div>
                <div className="col small">
                  <button type="button" className="btn btn-outline" onClick={() => removeMapping(idx)}>
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}
            <div className="mapping-actions">
              <button type="button" className="btn btn-outline" onClick={addMapping}>
                <i className="fas fa-plus"></i> Add column mapping
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={addAllColumns}
                disabled={!columns.length}
                title={!columns.length ? 'Load columns first' : 'Add mappings for all sheet columns'}
              >
                <i className="fas fa-list"></i> Add all columns
              </button>
            </div>
            {mappingError && (
              <div className="alert alert-error small"><i className="fas fa-exclamation-triangle"></i> {mappingError}</div>
            )}
          </div>
        )}
      </div>

      {error && <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      <style>{`
        .gs-config .field-row { margin-bottom: 12px; display: flex; flex-direction: column; align-items: stretch; gap: 6px; }
        .gs-config label { display: block; color: var(--text-secondary); font-weight: 600; }
        .gs-config input[type="text"], .gs-config select { width: 100%; padding: 8px; border-radius: 6px; border: 1px solid var(--glass-border, #ddd); background: var(--glass-bg,#fff); color: var(--text-primary,#111); }
        /* Make embedded GenericDropdown inputs expand to full width in this config */
        .gs-config .generic-dropdown .input-wrapper { width: 100%; }
        .gs-config .generic-dropdown input { width: 100%; }
        .gs-config .inline { display: flex; gap: 16px; }
        .gs-config .hint { color: var(--text-muted); font-size: 0.9rem; }
        .gs-config .mapping-list { display: flex; flex-direction: column; gap: 8px; }
        .gs-config .mapping-row { display: flex; gap: 8px; align-items: center; }
        .gs-config .mapping-row .col { flex: 1; }
        .gs-config .mapping-row .col.small { flex: 0 0 auto; }
        .gs-config .mapping-row .lbl { font-size: 0.8rem; color: var(--text-muted); margin-bottom: 4px; }
        .gs-config .mapping-actions { display: flex; gap: 8px; margin-top: 4px; }
        .gs-config .alert.small { font-size: 0.9rem; }
      `}</style>
    </div>
  );
};

export default GoogleSpreadsheetConfig;
